import { DatePicker, FormItem } from '@lmc/ui-kit'
import { styled } from 'styled-components'
import { Form, FormInstance, FormRule, TimePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { TIME_FORMAT } from '../utils.datetime'

interface DateAndTimeRowPickerProps {
  label: React.ReactNode
  name: string
  required?: boolean
  short?: boolean
  form?: FormInstance
  /** Form field for the earliest selectable calendar day (e.g. `'startDate.date'` for an end date). */
  minDateField?: string
}

/**
 * Two pickers for date and time, side by side.
 * The name of the date will be `${name}.date` and the name of the time will be `${name}.time`.
 */
export const DateAndTimeRowPicker = ({
  name,
  required,
  label,
  short,
  form,
  minDateField
}: DateAndTimeRowPickerProps) => {
  const contextForm = Form.useFormInstance()
  const resolvedForm = form ?? contextForm
  const [dateValue, setDateValue] = useState<Dayjs | null>(null)
  const dateDependencies = [
    `${name}.time`,
    ...(minDateField ? [minDateField] : [])
  ]

  const pickerDisabledDate = minDateField
    ? (current: Dayjs) => {
        const min = resolvedForm.getFieldValue(minDateField)
        if (!min) return false
        return current.isBefore(dayjs(min), 'day')
      }
    : undefined
  // Create a rule that requires both fields to be filled
  // Whenever one of the fields is filled, the other one will be validated
  const createCombinedRequiredRule =
    (dependency: string, error?: string): FormRule =>
    ({ getFieldValue, validateFields }) => ({
      validator(_, value) {
        const depValue = getFieldValue(dependency)
        if (value && !depValue) validateFields([dependency])
        if (!depValue || !value) {
          return Promise.reject(new Error(error))
        }
        return Promise.resolve()
      }
    })

  // Only the first field should show the error, the second is too narrow
  const combinedRequiredRuleDate = createCombinedRequiredRule(
    `${name}.time`,
    'Both date and time are required.'
  )
  const combinedRequiredRuleTime = createCombinedRequiredRule(`${name}.date`)

  useEffect(() => {
    if (!form) return

    const timeValue = form.getFieldValue(`${name}.time`)

    if (dateValue && !timeValue) {
      form.setFieldsValue({
        [`${name}.time`]:
          name === 'endDate'
            ? dayjs('23:59', TIME_FORMAT)
            : dayjs('00:00', TIME_FORMAT)
      })
    }
  }, [dateValue, form, name])

  return (
    <FormItemContainer label={label} required={required} short={short}>
      <DateTimeContainer>
        <DateContainer>
          <FormItem
            name={`${name}.date`}
            dependencies={dateDependencies}
            rules={required ? [combinedRequiredRuleDate] : undefined}
          >
            <DatePicker
              onChange={(value) => setDateValue(value)}
              disabledDate={pickerDisabledDate}
            />
          </FormItem>
        </DateContainer>
        <div>
          <FormItem
            name={`${name}.time`}
            dependencies={[`${name}.date`]}
            rules={required ? [combinedRequiredRuleTime] : undefined}
          >
            <TimePicker format="HH:mm" needConfirm={false} />
          </FormItem>
        </div>
      </DateTimeContainer>
    </FormItemContainer>
  )
}

const FormItemContainer = styled(FormItem)`
  margin-bottom: 0;
`

const DateTimeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  align-items: top;
`

const DateContainer = styled.div`
  display: flex;
  flex: 1;
  margin-right: 8px;
`
